// src/pages/documentType/DocumentTypeCreationPage.tsx

import React, { useCallback } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { SourceDocumentDefinition } from "../../types/documentTypes";
import { DocumentTypeProvider } from "../../components/documentType/context/DocumentTypeContext";
import { ValidationProvider } from "../../contexts/ValidationContext";
import { useWorkspaceContext } from "../../../../providers/WorkspaceProvider";
import DocumentTypeManager from "../../components/documentType/DocumentTypeManager";
import {
  useDocumentTypes,
  useFetchDocumentType,
  useFetchDocumentTypeVersions,
} from "../../hooks/useDocumentTypes";
import { useDocumentSources } from "../../hooks/useDocumentSources";
import {
  useCreateDocumentTypeInput,
  useUpdateDocumentTypeInput,
  useDocumentTypeInputs,
} from "../../hooks/useDocumentTypeInputs";
import {
  useCreateDocumentTypeSection,
  useUpdateDocumentTypeSection,
  useDeleteDocumentTypeSection,
  useDocumentTypeSections,
} from "../../hooks/useDocumentTypeSections";
import {
  DocumentTypeFormData,
  DocumentTypeCreateRequest,
} from "../../types/documentTypes";
import Page from "../../../../components/Pages/Page";
import PageHeader from "../../../../components/PageHeader";
import SubTitle from "../../../../components/SubTitle";
import { Button, Stack, Typography, IconButton, Tooltip } from "@mui/joy";
import { TabDefinition } from "../../../../types/tabs";
import {
  OpenInNew as OpenInNewIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { useDocumentTypePortability } from "../../hooks/useDocumentTypePortability";
import DocumentTypeVersionRollback from "./DocumentTypeVersionRollback";

// Define document type specific tab types
type DocumentTypeTab = "basic" | "templates" | "source" | "fields";

// Type guard for tab validation
function isValidTab(tab: string): tab is DocumentTypeTab {
  return ["basic", "templates", "source", "fields", "structure"].includes(tab);
}

// Tab definitions for document types
const DOCUMENT_TYPE_TABS: TabDefinition[] = [
  {
    id: "basic",
    label: "Basic Info",
    errorPattern: ["documentType.Name", "basic."],
  },
  {
    id: "templates",
    label: "Templates",
    errorPattern: ["template."],
  },
  {
    id: "source",
    label: "Source Documents",
    errorPattern: ["sourceDocument."],
  },
  {
    id: "fields",
    label: "Data Fields",
    errorPattern: ["field."],
  },
  {
    id: "structure",
    label: "Structure",
    errorPattern: ["section."],
  },
];

interface TabConfig {
  id: DocumentTypeTab;
  getData: () => Partial<DocumentTypeFormData>;
}

// Form data configuration
const TAB_CONFIGS: Record<DocumentTypeTab, TabConfig> = {
  basic: {
    id: "basic",
    getData: () => ({
      Name: "",
      Description: "",
      CustomInstructions: "",
    }),
  },
  templates: {
    id: "templates",
    getData: () => ({}), // Templates don't need form data
  },
  source: {
    id: "source",
    getData: () => ({
      document_type_sources: [],
      deletedSourceIds: [],
    }),
  },
  fields: {
    id: "fields",
    getData: () => ({
      document_type_inputs: [],
    }),
  },
};

const ManageDocumentType: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { workspace, isAdmin } = useWorkspaceContext();
  const documentTypeId = id ? parseInt(id, 10) : undefined;
  const { exportDocumentType, isExporting } = useDocumentTypePortability(
    workspace?.id,
  );
  const locationState = location.state as {
    isNewSession?: boolean;
    initialStep?: number;
  } | null;
  // Stays true after first-save navigate (isNewSession state) so step hints
  // continue showing on the remounted edit route.
  const isFirstTimeCreation = !documentTypeId || !!locationState?.isNewSession;
  // When navigating from /new → /:id after create, carry the target step so the
  // fresh mount starts at the right tab instead of resetting to step 0.
  const initialStep = locationState?.initialStep ?? 0;
  const initialTab = DOCUMENT_TYPE_TABS[initialStep]?.id;

  const {
    createDocumentType,
    updateDocumentType,
    rollbackDocumentTypeVersion,
    loading: documentTypeLoading,
  } = useDocumentTypes(workspace?.id || "");
  const { documentType } = useFetchDocumentType(
    documentTypeId || 0,
    workspace?.id || "",
    undefined,
    "Latest",
  );
  const { versions, error: versionsError } = useFetchDocumentTypeVersions(
    documentTypeId || 0,
    workspace?.id || "",
  );
  const {
    createSource,
    updateSource,
    deleteSource,
    getSources,
    loading: sourcesLoading,
  } = useDocumentSources();
  const { createInputField, loading: inputsLoading } =
    useCreateDocumentTypeInput(workspace?.id || "");
  const { updateInputField } = useUpdateDocumentTypeInput(workspace?.id || "");
  const { getInputFields } = useDocumentTypeInputs(workspace?.id || "");
  const { loading: createSectionLoading } = useCreateDocumentTypeSection(
    workspace?.id || "",
  );
  const { loading: updateSectionLoading } = useUpdateDocumentTypeSection(
    workspace?.id || "",
  );
  const { loading: deleteSectionLoading } = useDeleteDocumentTypeSection(
    workspace?.id || "",
  );
  const { getSections } = useDocumentTypeSections(workspace?.id || "");

  // Load source-document placeholders into editor state independently of the
  // Source Documents tab, so the Structure tab's per-section source selector
  // has data on first entry (mirrors loadSections / loadInputFields).
  const loadSources = useCallback(
    async (docTypeId: number): Promise<SourceDocumentDefinition[]> => {
      const sources = await getSources(docTypeId, workspace?.id || "");
      return sources.map((source) => ({
        SourceFieldID: source.SourceFieldID,
        Name: source.Name,
        Label: source.Label,
        DocumentType: source.DocumentType,
        IsRequired: source.IsRequired,
        OrderIndex: source.OrderIndex,
        HelpText: source.HelpText || "",
      }));
    },
    [getSources, workspace?.id],
  );

  const handleTabChange = async (
    currentTab: string,
    formData: Partial<DocumentTypeFormData>,
  ) => {
    if (!isValidTab(currentTab)) {
      console.error("Invalid tab:", currentTab);
      return;
    }

    // Safety check: if we have a documentTypeId, we should never try to create
    if (documentTypeId && currentTab === "basic") {
      try {
        await updateDocumentType(documentTypeId, {
          Name: formData.Name!,
          Description: formData.Description || "",
          CustomInstructions: formData.CustomInstructions || "",
          Status: "Draft", // Maintain draft status during edits
        });
        return; // Exit early after updating
      } catch (err: any) {
        console.error("Failed to update document type:", err);
        throw err; // Re-throw to be handled by the calling component
      }
    }

    // Ensure we're not trying to create when we should be updating
    if (currentTab === "basic" && !documentTypeId) {
      const createRequest: DocumentTypeCreateRequest = {
        Name: formData.Name!,
        Description: formData.Description || undefined,
        CustomInstructions: formData.CustomInstructions || undefined,
      };

      try {
        const result = await createDocumentType(createRequest);
        navigate(
          `/workspace/${workspace?.id}/meridian/admin/document-types/${result.DocumentTypeID}`,
          { replace: true, state: { isNewSession: true, initialStep: 1 } },
        );
      } catch (err: any) {
        console.error("Failed to create document type:", err);
        throw err; // Re-throw to be handled by the calling component
      }
    } else if (documentTypeId) {
      // We have a documentTypeId, so we should be updating, not creating
      if (currentTab === "source" && formData.document_type_sources) {
        try {
          const sources = formData.document_type_sources;
          const deletedIds = formData.deletedSourceIds || [];

          // Delete removed sources
          for (const id of deletedIds) {
            await deleteSource(documentTypeId, id, workspace?.id || "");
          }

          // Update/create remaining sources
          for (const source of sources) {
            if (source.SourceFieldID) {
              await updateSource(
                documentTypeId,
                source.SourceFieldID,
                {
                  Name: source.Name,
                  Label: source.Label,
                  DocumentType: source.DocumentType,
                  IsRequired: source.IsRequired,
                  OrderIndex: source.OrderIndex,
                  HelpText: source.HelpText,
                },
                workspace?.id || "",
              );
            } else {
              const { SourceFieldID, ...newSource } = source;
              await createSource(
                documentTypeId,
                newSource,
                workspace?.id || "",
              );
            }
          }
        } catch (err) {
          console.error("Failed to save source documents:", err);
          throw err;
        }
      } else if (currentTab === "fields" && formData.document_type_inputs) {
        try {
          const fields = formData.document_type_inputs;

          // Process existing fields (updates)
          const existingFields = fields.filter((field) => field.InputFieldID);
          for (const field of existingFields) {
            await updateInputField(documentTypeId, field.InputFieldID!, {
              Name: field.Name,
              Label: field.Label,
              InputType: field.InputType,
              Options: field.Options,
              ValidationRules: field.ValidationRules,
              IsRequired: field.IsRequired,
              OrderIndex: field.OrderIndex,
              HelpText: field.HelpText,
            });
          }

          // Process new fields (creates)
          const newFields = fields.filter((field) => !field.InputFieldID);
          const createPromises = newFields.map((field) =>
            createInputField(documentTypeId, field),
          );
          await Promise.all(createPromises);
        } catch (err) {
          console.error("Failed to save input fields:", err);
          throw err;
        }
      }
    } else {
      // Handle case where we have no documentTypeId but are not on basic tab
      console.warn(
        "Unexpected state: no documentTypeId but not on basic tab:",
        { currentTab, documentTypeId },
      );
    }
  };

  const handleSubmit = async (formData: DocumentTypeFormData) => {
    try {
      if (documentTypeId) {
        await updateDocumentType(documentTypeId, {
          Name: formData.Name,
          Description: formData.Description || "",
          CustomInstructions: formData.CustomInstructions || "",
          Status: "Draft", // Keep as Draft instead of Published
        });
      }
      navigate(`/workspace/${workspace?.id}/meridian/admin/document-types`);
    } catch (err) {
      console.error("Failed to complete document type:", err);
      throw err;
    }
  };

  const handleCancel = () => {
    navigate(`/workspace/${workspace?.id}/meridian/admin/document-types`);
  };

  const handleRollback = async (targetVersion: number) => {
    if (!documentTypeId || !workspace?.id) return;
    const result = await rollbackDocumentTypeVersion(
      documentTypeId,
      targetVersion,
    );
    window.location.replace(
      `/workspace/${workspace.id}/meridian/admin/document-types/${result.DocumentTypeID}`,
    );
  };

  return (
    <Page>
      <PageHeader>
        <Typography level="h1">
          {documentTypeId ? "Edit Document Type" : "Create Document Type"}
        </Typography>
        {documentType && (versions !== null || versionsError) && (
          <DocumentTypeVersionRollback
            documentType={documentType}
            versions={versions}
            loadError={
              versionsError
                ? "Could not load document type versions. Refresh and try again."
                : null
            }
            loading={documentTypeLoading}
            onRollback={handleRollback}
          />
        )}
        {documentTypeId && (
          <Stack direction="column" spacing={1}>
            {documentType?.CopiedFromWorkspaceId &&
              documentType?.CopiedFromWorkspaceName && (
                <Tooltip
                  title="Visit workspace where this document type was copied from"
                  placement="top"
                >
                  <IconButton
                    component={Link}
                    to={`/workspace/${documentType.CopiedFromWorkspaceId}/meridian/admin/document-types`}
                    size="sm"
                    sx={{ alignSelf: "flex-end" }}
                  >
                    <Typography level="body-sm" sx={{ fontWeight: "md" }}>
                      Visit source workspace
                    </Typography>
                    <OpenInNewIcon />
                  </IconButton>
                </Tooltip>
              )}
            <Stack direction="row" spacing={1}>
              {isAdmin && (
                <Button
                  variant="outlined"
                  color="neutral"
                  startDecorator={<DownloadIcon />}
                  loading={isExporting}
                  onClick={() =>
                    documentTypeId && exportDocumentType(documentTypeId)
                  }
                >
                  Export Document Type
                </Button>
              )}
              <Button
                component={Link}
                to={`/workspace/${workspace?.id}/meridian/admin/document-types/${documentTypeId}/test`}
              >
                Review Prompts
              </Button>
            </Stack>
          </Stack>
        )}
      </PageHeader>
      <SubTitle>
        {documentTypeId
          ? "Modify the existing document type configuration"
          : "Define a new document type with its fields and source documents"}
      </SubTitle>
      <ValidationProvider>
        <DocumentTypeProvider initialTab={initialTab}>
          <DocumentTypeManager
            steps={DOCUMENT_TYPE_TABS}
            stepConfigs={TAB_CONFIGS}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            onStepChange={handleTabChange}
            isSubmitting={
              documentTypeLoading ||
              sourcesLoading ||
              inputsLoading ||
              createSectionLoading ||
              updateSectionLoading ||
              deleteSectionLoading
            }
            documentTypeId={documentTypeId}
            workspace_id={workspace?.id || ""}
            loadInputFields={getInputFields}
            loadSections={getSections}
            loadSources={loadSources}
            isFirstTimeCreation={isFirstTimeCreation}
          />
        </DocumentTypeProvider>
      </ValidationProvider>
    </Page>
  );
};

export default ManageDocumentType;
